import React, {FC, useState} from "react";
import {Employee} from "../../../lib/src/api/dto/Employee"
import {useApi} from "../api/APIContext";
import { Input } from "@traas/lib/src/components/form/Input";
import { ModalFooter } from "@traas/lib/src/components/layout/ModalProvider";
import {faPlus} from "@fortawesome/free-solid-svg-icons";
import {ValidationError} from "../api/api";

export const EditEmergencyDetailsModal: FC<{ employee: Employee } > = ({employee}) => {
  const {updateEmployeeEmergencyDetails} = useApi()

  // Self fields
  const [emergencySelfName, setEmergencySelfName] = useState<string>(employee.metadata.emergencySelfName ?? '')
  const [emergencySelfStreetname, setEmergencySelfStreetname] = useState<string>(employee.metadata.emergencySelfStreetname ?? '')
  const [emergencySelfHouseNumber, setEmergencySelfHouseNumber] = useState<string>(employee.metadata.emergencySelfHouseNumber ?? '')
  const [emergencySelfHouseNumberAddition, setEmergencySelfHouseNumberAddition] = useState<string>(employee.metadata.emergencySelfHouseNumberAddition ?? '')
  const [emergencySelfCity, setEmergencySelfCity] = useState<string>(employee.metadata.emergencySelfCity ?? '')
  const [emergencySelfZipcode, setEmergencySelfZipcode] = useState<string>(employee.metadata.emergencySelfZipcode ?? '')
  const [emergencySelfDateOfBirth, setEmergencySelfDateOfBirth] = useState<Date>(employee.metadata.emergencySelfDateOfBirth ?? new Date())
  const [emergencySelfPhone, setEmergencySelfPhone] = useState<string>(employee.metadata.emergencySelfPhone ?? '')
  const [emergencySelfAllergies, setEmergencySelfAllergies] = useState<string>(employee.metadata.emergencySelfAllergies ?? '')
  const [emergencySelfNote, setEmergencySelfNote] = useState<string>(employee.metadata.emergencySelfNote ?? '')

  // Contact fields
  const [emergencyContactName, setEmergencyContactName] = useState<string>(employee.metadata.emergencyContactName ?? '')
  const [emergencyContactPhone, setEmergencyContactPhone] = useState<string>(employee.metadata.emergencyContactPhone ?? '')
  const [emergencyContactStreetname, setEmergencyContactStreetname] = useState<string>(employee.metadata.emergencyContactStreetname ?? '')
  const [emergencyContactHouseNumber, setEmergencyContactHouseNumber] = useState<string>(employee.metadata.emergencyContactHouseNumber ?? '')
  const [emergencyContactHouseNumberAddition, setEmergencyContactHouseNumberAddition] = useState<string>(employee.metadata.emergencyContactHouseNumberAddition ?? '')
  const [emergencyContactCity, setEmergencyContactCity] = useState<string>(employee.metadata.emergencyContactCity ?? '')
  const [emergencyContactZipcode, setEmergencyContactZipcode] = useState<string>(employee.metadata.emergencyContactZipcode ?? '')

  // GP fields
  const [emergencyGpName, setEmergencyGpName] = useState<string>(employee.metadata.emergencyGpName ?? '')
  const [emergencyGpPhone, setEmergencyGpPhone] = useState<string>(employee.metadata.emergencyGpPhone ?? '')
  const [emergencyGpStreetname, setEmergencyGpStreetname] = useState<string>(employee.metadata.emergencyGpStreetname ?? '')
  const [emergencyGpHouseNumber, setEmergencyGpHouseNumber] = useState<string>(employee.metadata.emergencyGpHouseNumber ?? '')
  const [emergencyGpHouseNumberAddition, setEmergencyGpHouseNumberAddition] = useState<string>(employee.metadata.emergencyGpHouseNumberAddition ?? '')
  const [emergencyGpZipcode, setEmergencyGpZipcode] = useState<string>(employee.metadata.emergencyGpZipcode ?? '')
  const [emergencyGpCity, setEmergencyGpCity] = useState<string>(employee.metadata.emergencyGpCity ?? '')
  const [errors, setErrors] = React.useState<string[]>([])

  const confirm = async () => {
    await updateEmployeeEmergencyDetails(
      employee.id,
      emergencySelfName,
      emergencySelfStreetname,
      emergencySelfHouseNumber,
      emergencySelfHouseNumberAddition,
      emergencySelfZipcode,
      emergencySelfCity,
      // If current year, the field was not edited, and it is the default value
      emergencySelfDateOfBirth.getFullYear() === new Date().getFullYear() ? null : emergencySelfDateOfBirth,
      emergencySelfPhone,

      emergencyContactName,
      emergencyContactPhone,
      emergencyContactStreetname,
      emergencyContactHouseNumber,
      emergencyContactHouseNumberAddition,
      emergencyContactZipcode,
      emergencyContactCity,

      emergencyGpName,
      emergencyGpPhone,
      emergencyGpStreetname,
      emergencyGpHouseNumber,
      emergencyGpHouseNumberAddition,
      emergencyGpZipcode,
      emergencyGpCity,
      emergencySelfAllergies,
      emergencySelfNote,
    ).catch(e => {
      if (e instanceof ValidationError) {
        setErrors(Object.values(e.errors).flat())
      }
      throw e
    })
  }

  return <form>
    <div className={"grid grid-cols-1 lg:grid-cols-2 gap-4"}>

      <div className={'flex-col space-y-2 border border-slate-200 px-4 py-3 rounded-lg'}>
        <h2 className={'font-medium mb-4'}>Medewerker</h2>
          <Input type={'text'} label={'Naam'} value={emergencySelfName} onChange={setEmergencySelfName} />
          <Input type={'date'} label={'Geboortedatum'} value={emergencySelfDateOfBirth} onChange={setEmergencySelfDateOfBirth} />
        <div className={'grid grid-cols-4 gap-4'}>
          <div className={'col-span-2'}><Input type={'text'} label={'Straatnaam'} value={emergencySelfStreetname} onChange={setEmergencySelfStreetname} /></div>
          <div className={'col-span-1'}><Input type={'text'} label={'Huisnummer'} value={emergencySelfHouseNumber} onChange={setEmergencySelfHouseNumber} /></div>
          <div className={'col-span-1'}><Input type={'text'} label={'Toevoeging'} value={emergencySelfHouseNumberAddition} onChange={setEmergencySelfHouseNumberAddition} /></div>
        </div>
        <div className={'grid grid-cols-2 gap-4'}>
          <div className={'col-span-1'}><Input type={'text'} label={'Stad'} value={emergencySelfCity} onChange={setEmergencySelfCity} /></div>
          <div className={'col-span-1'}><Input type={'text'} label={'Postcode'} value={emergencySelfZipcode} onChange={setEmergencySelfZipcode} /></div>
        </div>
        <Input type={'text'} label={'Telefoonnummer'} value={emergencySelfPhone} onChange={setEmergencySelfPhone} />
        <Input type={"text"} label={'Allergieën'} value={emergencySelfAllergies} onChange={setEmergencySelfAllergies} />
        <Input type={"textarea"} label={'Notities'} value={emergencySelfNote} onChange={setEmergencySelfNote} />
      </div>

      <div className={'lg:col-span-1 flex flex-col space-y-4'}>
        <div className={'flex-col space-y-2 border border-slate-200 px-4 py-3 rounded-lg'}>
          <h2 className={'font-medium mb-4'}>Noodcontact</h2>
          <Input type={'text'} label={'Naam'} value={emergencyContactName} onChange={setEmergencyContactName} />
          <div className={'grid grid-cols-4 gap-4'}>
            <div className={'col-span-2'}><Input type={'text'} label={'Straatnaam'} value={emergencyContactStreetname} onChange={setEmergencyContactStreetname} /></div>
            <div className={'col-span-1'}><Input type={'text'} label={'Huisnummer'} value={emergencyContactHouseNumber} onChange={setEmergencyContactHouseNumber} /></div>
            <div className={'col-span-1'}><Input type={'text'} label={'Toevoeging'} value={emergencyContactHouseNumberAddition} onChange={setEmergencyContactHouseNumberAddition} /></div>
          </div>
          <div className={'grid grid-cols-2 gap-4'}>
            <div className={'col-span-1'}><Input type={'text'} label={'Stad'} value={emergencyContactCity} onChange={setEmergencyContactCity} /></div>
            <div className={'col-span-1'}><Input type={'text'} label={'Postcode'} value={emergencyContactZipcode} onChange={setEmergencyContactZipcode} /></div>
          </div>
          <Input type={'text'} label={'Telefoonnummer'} value={emergencyContactPhone} onChange={setEmergencyContactPhone} />
        </div>

        <div className={'flex-col space-y-2 border border-slate-200 px-4 py-3 rounded-lg'}>
          <h2 className={'font-medium mb-4'}>Huisarts</h2>
          <Input type={"text"} label={'Naam'} value={emergencyGpName} onChange={setEmergencyGpName} />
          <div className={'grid grid-cols-4 gap-4'}>
            <div className={'col-span-2'}><Input type={'text'} label={'Straatnaam'} value={emergencyGpStreetname} onChange={setEmergencyGpStreetname} /></div>
            <div className={'col-span-1'}><Input type={'text'} label={'Huisnummer'} value={emergencyGpHouseNumber} onChange={setEmergencyGpHouseNumber} /></div>
            <div className={'col-span-1'}><Input type={'text'} label={'Toevoeging'} value={emergencyGpHouseNumberAddition} onChange={setEmergencyGpHouseNumberAddition} /></div>
          </div>
          <div className={'grid grid-cols-2 gap-4'}>
            <div className={'col-span-1'}><Input type={'text'} label={'Stad'} value={emergencyGpCity} onChange={setEmergencyGpCity} /></div>
            <div className={'col-span-1'}><Input type={'text'} label={'Postcode'} value={emergencyGpZipcode} onChange={setEmergencyGpZipcode} /></div>
          </div>
          <Input type={"text"} label={'Telefoonnummer'} value={emergencyGpPhone} onChange={setEmergencyGpPhone} />
        </div>
      </div>

      {errors.length > 0 && <div className={"text-red-600 text-sm"}>
        {errors.map((e, i) => <p key={i}>{e}</p>)}
      </div>}
    </div>
    <ModalFooter icon={faPlus} text={'Opslaan'} onSubmit={confirm} />
  </form>

}